import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Button, Form, Row, Col, Modal } from 'react-bootstrap';
import FalconCardHeader from '../../../common/FalconCardHeader';

const ProfileInfo = ({ user, onUpdate, updating }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    fullName: user?.fullName || ''
  });

  const handleEdit = () => {
    setEditData({
      fullName: user?.fullName || ''
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    const success = await onUpdate(editData);
    if (success) {
      setShowEditModal(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calcular posición basada en roles
  const getUserPosition = (roles) => {
    if (!roles || roles.length === 0) return 'Usuario';
    
    const roleHierarchy = {
      'SUPER_ADMIN': 'Super Administrador',
      'ADMIN': 'Administrador',
      'MANAGER': 'Gerente',
      'USER': 'Usuario',
      'VIEWER': 'Visualizador'
    };

    for (const roleKey of Object.keys(roleHierarchy)) {
      if (roles.some(role => role.name === roleKey)) {
        return roleHierarchy[roleKey];
      }
    }

    return roles[0].name;
  };

  if (!user) return null;

  return (
    <>
      <Card className="mb-3">
        <FalconCardHeader 
          title="Información Personal"
          endEl={
            <Button 
              variant="falcon-default" 
              size="sm" 
              onClick={handleEdit}
              disabled={updating}
            >
              <FontAwesomeIcon icon="edit" className="me-1" />
              Editar
            </Button>
          }
        />

        <Card.Body className="bg-body-tertiary">
          <Row className="g-3">
            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-semibold text-muted">
                  <FontAwesomeIcon icon="user" className="me-1" />
                  Nombre de Usuario
                </Form.Label>
                <p className="mb-0 text-900">{user.username}</p>
              </div>
            </Col>
            
            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-semibold text-muted">
                  <FontAwesomeIcon icon="envelope" className="me-1" />
                  Correo Electrónico
                </Form.Label>
                <p className="mb-0 text-900">{user.email}</p>
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-semibold text-muted">
                  <FontAwesomeIcon icon="id-card" className="me-1" />
                  Nombre Completo
                </Form.Label>
                <p className="mb-0 text-900">{user.fullName}</p>
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-semibold text-muted">
                  <FontAwesomeIcon icon="briefcase" className="me-1" />
                  Cargo
                </Form.Label>
                <p className="mb-0 text-900">{getUserPosition(user.roles)}</p>
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-semibold text-muted">
                  <FontAwesomeIcon icon="building" className="me-1" />
                  Empresa
                </Form.Label>
                <p className="mb-0 text-900">{user.company?.name || 'No definida'}</p>
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-semibold text-muted">
                  <FontAwesomeIcon icon="folder" className="me-1" />
                  Departamentos
                </Form.Label>
                <p className="mb-0 text-900">
                  {user.businessAreas?.map(ba => ba.name).join(', ') || 'Sin asignar'}
                </p>
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-semibold text-muted">
                  <FontAwesomeIcon icon="calendar-plus" className="me-1" />
                  Fecha de Registro
                </Form.Label>
                <p className="mb-0 text-900">{formatDate(user.createdAt)}</p>
              </div>
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Form.Label className="fw-semibold text-muted">
                  <FontAwesomeIcon icon="clock" className="me-1" />
                  Última Actividad
                </Form.Label>
                <p className="mb-0 text-900">{formatDate(user.updatedAt)}</p>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Modal de Edición */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon="edit" className="me-2 text-primary" />
            Editar Información Personal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <FontAwesomeIcon icon="id-card" className="me-1 text-primary" />
                Nombre Completo
              </Form.Label>
              <Form.Control
                type="text"
                value={editData.fullName}
                onChange={(e) => setEditData({...editData, fullName: e.target.value})}
                placeholder="Ingresa tu nombre completo"
              />
              <Form.Text className="text-muted">
                Este nombre se mostrará en tu perfil público y en las comunicaciones del sistema.
              </Form.Text>
            </Form.Group>

            <div className="bg-light p-3 rounded">
              <small className="text-muted">
                <FontAwesomeIcon icon="info-circle" className="me-1" />
                <strong>Nota:</strong> El nombre de usuario y correo electrónico solo pueden ser 
                modificados por un administrador del sistema.
              </small>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowEditModal(false)}
            disabled={updating}
          >
            <FontAwesomeIcon icon="times" className="me-1" />
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSave}
            disabled={updating || !editData.fullName.trim()}
          >
            {updating ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Guardando...</span>
                </div>
                Guardando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="save" className="me-1" />
                Guardar Cambios
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProfileInfo; 